import { apiTransport } from './api-client';
import type { Page } from './collections';

/**
 * Registered phones, for whoever reviews attendance.
 *
 * An employee's first phone registers itself; every further one waits here
 * until somebody holding `attendance.review` decides whether it counts as
 * theirs. The server caps the list at 200 rows and filters by status and a
 * name search, so paging is done on what it returns rather than by asking
 * again — the pending queue is short by nature.
 */

export type DeviceStatus = 'pending' | 'registered' | 'revoked';

interface ApiDevice {
  id: string;
  deviceIdentifier: string;
  platform: string;
  model: string | null;
  deviceName: string | null;
  osVersion: string | null;
  appVersion: string | null;
  status: DeviceStatus;
  registeredAt: string;
  lastSeenAt: string;
  /** The last attendance the phone recorded for its owner; null if it never has. */
  lastUsedAt?: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  /* Who approved and who revoked it, from the audit trail; absent from an older server. */
  approval?: DeviceDecision | null;
  revocation?: DeviceRevocation | null;
  user: {
    id: string;
    displayName: string;
    email: string;
    employee: { id: string; fullName: string; employeeNumber: string } | null;
  } | null;
}

/**
 * A decision on a device: who took it, when, and why. `automatic` is an
 * account's first phone, accepted on registration without anybody deciding.
 */
export interface DeviceDecision {
  automatic: boolean;
  at: string | null;
  byName: string | null;
  reason: string | null;
}

/**
 * A phone taken out of use: `rejected` while it was still waiting for
 * approval, `revoked` once it had been approved.
 */
export interface DeviceRevocation extends Omit<DeviceDecision, 'automatic'> {
  outcome?: 'rejected' | 'revoked';
}

export interface DeviceRow {
  id: string;
  employeeName: string;
  employeeNumber: string | null;
  email: string | null;
  /** What the employee called it, else the model. */
  name: string;
  model: string | null;
  platform: string;
  osVersion: string | null;
  appVersion: string | null;
  status: DeviceStatus;
  submittedAt: string;
  /** The last attendance recorded from it — what "Last used" means. */
  lastUsedAt: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  approval: DeviceDecision | null;
  revocation: DeviceRevocation | null;
}

const PLATFORM: Record<string, string> = { ios: 'iOS', android: 'Android', web: 'Web' };

export function platformLabel(platform: string): string {
  return PLATFORM[platform] ?? platform;
}

/**
 * The operating system as one phrase: "Android 14", "iOS 18.6".
 *
 * Phones report their version inconsistently — some send "Android 14", some
 * "14" — so the platform name is taken off the front before it is put back,
 * rather than reading "Android Android 14".
 */
export function systemLabel(platform: string | null, osVersion: string | null): string | null {
  const name = platform ? platformLabel(platform) : null;
  const version = (osVersion ?? '').trim();
  if (!version) return name;
  if (!name) return version;
  const bare = version.replace(new RegExp(`^(${name}|${platform})\\s*`, 'i'), '').trim();
  return bare ? `${name} ${bare}` : name;
}

/** What to call a handset nobody named and whose model is unknown. */
export function phoneLabel(platform: string | null): string {
  if (platform === 'ios') return 'iPhone';
  if (platform === 'android') return 'Android phone';
  return 'Phone';
}

function toRow(device: ApiDevice): DeviceRow {
  return {
    id: device.id,
    employeeName: device.user?.employee?.fullName ?? device.user?.displayName ?? 'Unknown employee',
    employeeNumber: device.user?.employee?.employeeNumber ?? null,
    email: device.user?.email ?? null,
    name: device.deviceName ?? device.model ?? phoneLabel(device.platform),
    model: device.model,
    platform: device.platform,
    osVersion: device.osVersion,
    appVersion: device.appVersion,
    status: device.status,
    submittedAt: device.registeredAt,
    // An older server without the figure: the registration's own last contact.
    lastUsedAt: device.lastUsedAt === undefined ? device.lastSeenAt : device.lastUsedAt,
    revokedAt: device.revokedAt,
    revokedReason: device.revokedReason,
    approval: device.approval ?? null,
    revocation: device.revocation ?? null,
  };
}

export const devicesService = {
  async list(input: {
    status?: string;
    search?: string;
    page: number;
    pageSize: number;
  }): Promise<Page<DeviceRow>> {
    const devices = await apiTransport().get<ApiDevice[]>('/api/v1/devices', {
      query: {
        status: input.status && input.status !== 'all' ? input.status : undefined,
        search: input.search || undefined,
      },
    });
    const rows = devices.map(toRow);
    const start = (input.page - 1) * input.pageSize;
    return {
      rows: rows.slice(start, start + input.pageSize),
      total: rows.length,
      page: input.page,
      pageSize: input.pageSize,
    };
  },

  /** `reject` is the server's `revoke`: the phone cannot record attendance. */
  async decide(id: string, decision: 'approve' | 'reject', reason: string): Promise<void> {
    await apiTransport().patch(`/api/v1/devices/${encodeURIComponent(id)}`, {
      decision: decision === 'approve' ? 'approve' : 'revoke',
      reason,
    });
  },
};
